import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Drawer,
  Fade,
  Tab,
  Tabs,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { RefHandles, SpringEvent } from 'react-spring-bottom-sheet/dist/types';
import { Keyboard } from '@capacitor/keyboard';
import { useTranslation } from 'react-i18next';
import { usePoisState } from 'state/usePoisState';
import { Capacitor } from '@capacitor/core';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useForegroundState } from 'hooks/useForegroundState';
import { usePrevious } from 'hooks/usePrevious';
import { useVisualViewportHeight } from 'hooks/useVisualViewportHeight';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { PhotoModeration } from 'components/IncidentsMap/SearchDrawer/components/PhotoModeration';
import { IosAppSwitcherHandler } from 'components/IosAppSwitcherHandler';
import { useModerationReports } from 'components/IncidentsMap/SearchDrawer/hooks/useModerationReports';
import { useSearchDrawerTabs } from 'components/IncidentsMap/SearchDrawer/hooks/useSearchDrawerTabs';
import { useDeferredDrawerContent } from 'components/IncidentsMap/SearchDrawer/hooks/useDeferredDrawerContent';
import { SEARCH_DRAWER_TAB_PARAM_KEY } from 'components/IncidentsMap/SearchDrawer/SearchDrawer.constants';
import { useTabs } from 'hooks/useTabs';
import { useWaitForElement } from 'hooks/useWaitForElement';
import { useDrawerHeightListener } from 'features/mapping/hooks/useDrawerHeightListener';
import { MAP_ENTITY_DRAWER_MAX_SNAP_PCT } from 'features/mapping/components/MapEntityDrawer';
import { useKeepMaximizedOnKeyboardHide } from 'components/IncidentsMap/SearchDrawer/hooks/useKeepMaximizedOnKeyboardHide';
import {
  SEARCH_DRAWER_ID,
  SEARCH_DRAWER_WIDTH,
  getSearchCardMinSnapPx,
} from '../../../constants';
import { SearchResults } from './SearchResults';
import { SearchBar } from './SearchBar';
import { useRestoreEntityDrawerAfterSearchSelection } from './hooks/useRestoreEntityDrawerAfterSearchSelection';

const useStyles = makeStyles<{ open: boolean }>()((theme, { open }) => ({
  drawerContainer: {
    position: 'relative',
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    transform: `translateX(${SEARCH_DRAWER_WIDTH}px)`,
    ...(open && {
      transition: theme.transitions.create('transform', {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.enteringScreen,
      }),
      transform: 'translateX(0px)',
    }),
  },
  drawer: {
    flexShrink: 0,
    height: '100%',
    width: SEARCH_DRAWER_WIDTH,
  },
  drawerPaper: {
    paddingBottom: `env(safe-area-inset-bottom)`,
    width: SEARCH_DRAWER_WIDTH,
    position: 'relative !important' as 'relative',
    boxShadow: theme.shadows[16],
  },
  pullTab: {
    position: 'absolute',
    top: 'calc(50% - 30px)',
    left: -24,
    height: 60,
    width: 25,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[16],
    clipPath: 'inset(-32px 0 -32px -32px)',
    zIndex: theme.zIndex.drawer,
    borderTopLeftRadius: Number(theme.shape.borderRadius) * 1.34,
    borderBottomLeftRadius: Number(theme.shape.borderRadius) * 1.34,
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary,
  },
  mobileDrawer: {
    '& [data-rsbs-header]': {
      paddingTop: 8,
      '&:before': {
        top: 6,
        backgroundColor: theme.palette.alert.neutral.background,
      },
    },
  },
}));

/**
 * Wraps children in a plain div so `Fade` always receives a single
 * ref-forwardable DOM node, regardless of what children returns.
 */
const FadeContainer = ({
  in: inProp,
  children,
}: {
  in: boolean;
  children: React.ReactNode;
}): JSX.Element => (
  <Fade in={inProp} unmountOnExit>
    <div>{children}</div>
  </Fade>
);

const minimizeSheet = (sheetRef: MutableRefObject<RefHandles | null>): void => {
  sheetRef.current?.snapTo(({ snapPoints }) => Math.min(...snapPoints));
};

const maximizeSheet = (sheetRef: MutableRefObject<RefHandles | null>): void => {
  sheetRef.current?.snapTo(({ snapPoints }) => Math.max(...snapPoints));
};

export const SearchDrawer = (): JSX.Element => {
  const { open, toggleOpen, setMaximized } = useSearchDrawerState();
  const { classes } = useStyles({ open });
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const focusRef = useRef(null);
  const sheetRef = useRef<RefHandles | null>(null);
  const { clearSelectedPois } = usePoisState();
  const { t } = useTranslation();
  const appState = useForegroundState();
  const prevAppState = usePrevious(appState);
  const selectedGeoEventId = useSelectedGeoEventId();
  const mobileScrollbarRef = useRef<HTMLElement | null>(null);
  const desktopScrollbarRef = useRef<HTMLDivElement | null>(null);
  const viewportHeight = useVisualViewportHeight();
  const showPhotosTab = useSearchDrawerTabs();
  const { reportsCount } = useModerationReports();

  useRestoreEntityDrawerAfterSearchSelection();

  const searchCardMinSnapPx = getSearchCardMinSnapPx();

  // Largest viewport height seen so far, i.e. the height with no keyboard
  // open. A keyboard-driven shrink never lowers it.
  const baseViewportHeightRef = useRef(viewportHeight);
  if (viewportHeight > baseViewportHeightRef.current) {
    baseViewportHeightRef.current = viewportHeight;
  }

  // This drawer contains a focusable input (the search field), so opening it
  // brings up the on-screen keyboard, which shrinks the visible viewport on
  // both iOS and Android. Since the sheet is anchored to the bottom of that
  // viewport, a percentage-based max snap would ride the sheet's top edge up
  // past where the user pulled it. Instead hold a constant top inset (derived
  // from the keyboard-closed height) so the sheet stays put and simply fits
  // above the keyboard. When no keyboard is open `viewportHeight === base`, so
  // this equals `viewportHeight * MAX_SNAP_PCT`.
  const topInset =
    baseViewportHeightRef.current * (1 - MAP_ENTITY_DRAWER_MAX_SNAP_PCT);
  const maxSnap = Math.max(
    searchCardMinSnapPx,
    Math.round(viewportHeight - topInset),
  );

  // Prevents the sheet from collapsing when the keyboard hides after tapping
  // an in-drawer control.
  const { snapPoints, lockSheetOnKeyboardDismiss, unlockSheet } =
    useKeepMaximizedOnKeyboardHide({
      minSnap: searchCardMinSnapPx,
      maxSnap,
      viewportHeight,
      baseViewportHeight: baseViewportHeightRef.current,
    });

  const [sheetOpen, setSheetOpen] = useState(false);

  const showResults = useDeferredDrawerContent(open);

  // Toggle content off the sheet's live height so visibility stays in step
  // with the sheet motion instead of lagging at spring start/end.
  const drawerElement = useWaitForElement(
    isLargeMediaQuery ? null : `#${SEARCH_DRAWER_ID}`,
  );

  const handleSheetHeightChange = useCallback(
    (heightPx: number): void => {
      const displayThreshold = (searchCardMinSnapPx + maxSnap) / 3;
      const isOpen = heightPx >= displayThreshold;
      setSheetOpen((prev) => (prev === isOpen ? prev : isOpen));
    },
    [searchCardMinSnapPx, maxSnap],
  );

  useDrawerHeightListener(drawerElement, null, handleSheetHeightChange);

  useEffect(() => {
    /**
     * Close bottom sheet whenever there's an app state change from
     * foreground to background or when there's a selected geo event.
     * This case handles geoevents selected programmatically and not
     * by a user's interaction, e.g. tapping on a push notification.
     */
    if (selectedGeoEventId || (prevAppState?.isActive && !appState.isActive)) {
      if ((sheetRef.current?.height ?? 0) > searchCardMinSnapPx) {
        setTimeout(() => {
          minimizeSheet(sheetRef);
        }, 0);
      }
    }
  }, [searchCardMinSnapPx, prevAppState, appState, selectedGeoEventId]);

  const handleSpringEnd = (): void => {
    if (sheetRef.current) {
      const isMax = sheetRef.current.height > searchCardMinSnapPx;
      setSheetOpen(isMax);
      setMaximized(isMax);
    }
  };

  const handleSpringStart = (event: SpringEvent): void => {
    if (event.type === 'OPEN') {
      return;
    }

    mobileScrollbarRef.current = document.querySelector(
      `#${SEARCH_DRAWER_ID} [data-rsbs-scroll]`,
    );

    clearSelectedPois();
  };

  const handleCloseBottomSheet = (): void => {
    // Restore the min snap point so the sheet can fully collapse.
    unlockSheet();

    if (Capacitor.isNativePlatform()) {
      Keyboard.hide();
    }

    // Defer: snapTo clamps to the last-committed snap points, so the min must
    // be restored (above) and committed before we can snap to it.
    setTimeout(() => minimizeSheet(sheetRef), 0);
  };

  // This is only used for results that don't link to a route (i.e., places that drop a pin)
  const handleClickResult = (): void => {
    unlockSheet();
    setTimeout(() => {
      minimizeSheet(sheetRef);
    }, 300);
  };

  const handleSearchFocus = (): void => {
    maximizeSheet(sheetRef);
  };

  const tabData = [
    {
      tabId: 'incidents',
      label: t('home.searchDrawer.tabs.incidents'),
      enabled: true,
      content: (
        <>
          <SearchBar scrollbarRef={desktopScrollbarRef} />
          <FadeContainer in={showResults}>
            <SearchResults />
          </FadeContainer>
        </>
      ),
    },
    {
      tabId: 'photoModeration',
      label: t('home.searchDrawer.tabs.photos'),
      adornment: reportsCount ? (
        <Badge badgeContent={reportsCount} />
      ) : undefined,
      enabled: true,
      content: (
        <FadeContainer in={showResults}>
          <PhotoModeration />
        </FadeContainer>
      ),
    },
  ];

  const { tabsProps, tabProps, tabPanels } = useTabs({
    idPrefix: 'search-drawer',
    ariaLabel: t('home.searchDrawer.tabs.incidents'),
    tabData,
    queryParam: SEARCH_DRAWER_TAB_PARAM_KEY,
  });

  if (isLargeMediaQuery) {
    return (
      <Box
        className={classes.drawerContainer}
        aria-label={t('searchDrawer.ariaLabel')}
      >
        <Drawer
          classes={{ root: classes.drawer, paper: classes.drawerPaper }}
          variant="persistent"
          anchor="right"
          open={open}
        >
          <Box
            sx={{
              overflow: 'auto',
              maxHeight: '100%',
            }}
            ref={desktopScrollbarRef}
          >
            {showPhotosTab ? (
              <>
                <Tabs {...tabsProps}>
                  {tabProps.map((tProps) => {
                    const { key, ...rest } = tProps;
                    return <Tab key={key} {...rest} />;
                  })}
                </Tabs>
                {tabPanels}
              </>
            ) : (
              <>
                <SearchBar scrollbarRef={desktopScrollbarRef} />
                <FadeContainer in={showResults}>
                  <SearchResults />
                </FadeContainer>
              </>
            )}
          </Box>
        </Drawer>
        <button
          type="button"
          className={classes.pullTab}
          onClick={toggleOpen}
          aria-label={
            open
              ? t('home.searchDrawer.pullTab.close')
              : t('home.searchDrawer.pullTab.open')
          }
        >
          {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>
      </Box>
    );
  }

  return (
    <BottomSheet
      id={SEARCH_DRAWER_ID}
      open
      skipInitialTransition
      sibling={<IosAppSwitcherHandler />}
      ref={sheetRef}
      initialFocusRef={focusRef}
      onDismiss={handleCloseBottomSheet}
      snapPoints={() => snapPoints}
      defaultSnap={() => Math.min(...snapPoints)}
      expandOnContentDrag
      blocking={false}
      header
      onSpringEnd={handleSpringEnd}
      onSpringStart={handleSpringStart}
      // Cap to the visible viewport (excludes the on-screen keyboard) so snap
      // points track orientation changes and the keyboard.
      maxHeight={Math.round(viewportHeight)}
      className={classes.mobileDrawer}
    >
      <SearchBar
        onCancel={handleCloseBottomSheet}
        scrollbarRef={mobileScrollbarRef}
        onFocus={handleSearchFocus}
        onBeforeFocusDrawerElement={lockSheetOnKeyboardDismiss}
        isSheetOpen={sheetOpen}
      />
      {sheetOpen && (
        <SearchResults
          onBeforeClickResult={handleClickResult}
          isSheetOpen={sheetOpen}
          onBeforeFocusDrawerElement={lockSheetOnKeyboardDismiss}
        />
      )}
    </BottomSheet>
  );
};
